import { motion } from "framer-motion";
import { CheckCircle2, Bug, Copy, ExternalLink } from "lucide-react";
import { StatusBadge, SeverityTag } from "@/components/ui/Badge";

// Final result bubble: PASSED → green note; FAILED → structured bug card.
// Always shows the cost chip + a deep link back to the full dashboard report.
export default function ResultCard({ result }) {
  const failed = result.verdict === "FAILED";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      <div className="flex items-center justify-between">
        <StatusBadge status={result.verdict} />
        <span className="text-xs text-slate-400">
          ⏱ {result.duration} · {result.cost} · {result.tokens} tok
        </span>
      </div>

      {failed ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-3.5 dark:border-rose-500/20 dark:bg-rose-500/10">
          <div className="flex items-start justify-between gap-2">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white">
              <Bug size={15} className="text-rose-500" />
              {result.bug.title}
            </span>
            <SeverityTag severity={result.bug.severity} />
          </div>
          <dl className="mt-2.5 space-y-1.5 text-xs">
            <div>
              <dt className="inline font-semibold text-slate-500 dark:text-slate-400">
                Expected:{" "}
              </dt>
              <dd className="inline text-slate-600 dark:text-slate-300">
                {result.bug.expected}
              </dd>
            </div>
            <div>
              <dt className="inline font-semibold text-slate-500 dark:text-slate-400">
                Actual:{" "}
              </dt>
              <dd className="inline text-slate-600 dark:text-slate-300">
                {result.bug.actual}
              </dd>
            </div>
          </dl>
        </div>
      ) : (
        <div className="flex items-start gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3.5 dark:border-emerald-500/20 dark:bg-emerald-500/10">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-500" />
          <p className="text-sm text-slate-700 dark:text-slate-200">{result.note}</p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
          <Copy size={13} /> Copy
        </button>
        <button className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 dark:border-white/10 dark:text-brand-300 dark:hover:bg-white/5">
          <ExternalLink size={13} /> View full report
        </button>
      </div>
      <p className="text-center text-[0.6875rem] text-slate-400">
        Saved to your team&apos;s runs ✓
      </p>
    </motion.div>
  );
}
